/**
 * Sidebar component — Karr Design System v2.
 *
 * Accordion navigation with zone section headers. On a laptop it is all there
 * or not there at all (sidecentre, 5 Oct 2026): shut, the page gets the whole
 * width and SideDrawer's handle, halfway down the edge, brings it back.
 * Click a group label → expands to show sub-items.
 * Active route's group auto-expands.
 *
 * Icons feature Apple-style micro-animations on click —
 * each icon briefly bounces/spins/pulses when tapped.
 *
 * @since 2.0.0
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Star } from 'lucide-react';
import type { SidebarProps, NavZone, NavItem } from './sidebarNav';
import { triggerIconAnim, type IconAnimType } from '../hooks/useIconClick';
import { getFavoriteHrefs, toggleFavoriteHref, isFavoriteHref } from './navFavorites';
import { PlanBadge } from './PlanBadge';
import { SideDrawer } from './SideDrawer';
import { SIDEBAR_OPEN_EVENT } from '../layouts/sidebarOpen';


// Re-export types for consumers
export type { NavItem, NavGroup, NavZone, SidebarProps } from './sidebarNav';

/**
 * Pick the right animation type based on group id / label.
 * This gives each icon a contextually appropriate feel.
 */
function getAnimForGroup(groupId: string): IconAnimType {
    // Gear/cog → spin
    if (groupId.includes('settings') || groupId.includes('practice')) return 'spin';
    // Dashboard/home → pulse-pop
    if (groupId.includes('dashboard')) return 'pulse';
    // Tools → wiggle
    if (groupId.includes('tools') || groupId.includes('ai')) return 'wiggle';
    // Default → spring bounce
    return 'bounce';
}

/**
 * Find which group contains the active route.
 */
function findActiveGroupId(zones: NavZone[], activeRoute: string): string | null {
    for (const zone of zones) {
        for (const group of zone.groups) {
            if (group.items.some(item => item.href === activeRoute)) {
                return group.id;
            }
        }
    }
    return null;
}

export function Sidebar({ zones, activeRoute, onNavigate, onMobileClose, forceExpanded, autoCollapse, pinnableItems }: SidebarProps) {
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('karr-sidebar-collapsed') === 'true'; }
        catch { return false; }
    });

    // cc-10: on firm-altitude (Practice) routes the entity nav steps back out
    // of the way on its own. The handle still opens it — as a PEEK for this
    // visit, never persisted; descending into an entity restores the user's
    // own preference untouched.
    const [peeked, setPeeked] = useState(false);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { setPeeked(false); }, [autoCollapse]);

    // switchside: a switch to another client opens the nav, so "where to now
    // for him" is the first thing on screen (AppLayout decides when).
    useEffect(() => {
        const open = () => {
            setCollapsed(false);
            try { localStorage.setItem('karr-sidebar-collapsed', 'false'); } catch { /* noop */ }
        };
        window.addEventListener(SIDEBAR_OPEN_EVENT, open);
        return () => window.removeEventListener(SIDEBAR_OPEN_EVENT, open);
    }, []);

    const setOpen = useCallback((open: boolean) => {
        if (autoCollapse) { setPeeked(open); return; }
        setCollapsed(!open);
        try { localStorage.setItem('karr-sidebar-collapsed', String(!open)); } catch { /* noop */ }
    }, [autoCollapse]);

    // ─── Favorites (pinned shortcuts) ───
    const [favorites, setFavorites] = useState<string[]>(() => getFavoriteHrefs());

    // Flat index of every nav item by href, for resolving favorites + dedupe.
    // ob-2: `pinnableItems` adds destinations that are reachable but have no row
    // of their own (Simple's hub-strip screens), so a pin survives its item
    // leaving the sidebar. Real nav rows win on conflict — they carry badges.
    const itemsByHref = useMemo(() => {
        const map = new Map<string, NavItem>();
        for (const zone of zones) {
            for (const group of zone.groups) {
                for (const item of group.items) {
                    if (!map.has(item.href)) map.set(item.href, item);
                }
            }
        }
        for (const item of pinnableItems ?? []) {
            if (!map.has(item.href)) map.set(item.href, item);
        }
        return map;
    }, [zones, pinnableItems]);

    // Resolve favorite hrefs to live items, preserving pin order, dropping any
    // that no longer exist in the current (level/nature-filtered) nav.
    const favoriteItems = useMemo(
        () => favorites.map(href => itemsByHref.get(href)).filter((i): i is NavItem => Boolean(i)),
        [favorites, itemsByHref],
    );

    const handleToggleFavorite = useCallback((href: string) => {
        setFavorites(toggleFavoriteHref(href));
    }, []);

    // Which group is expanded (auto-expand the active route's group)
    const activeGroupId = useMemo(() => findActiveGroupId(zones, activeRoute), [zones, activeRoute]);

    // Groups inside an accent zone (the Practice command centre) stay a compact
    // collapsed "dropdown" — they don't auto-expand on their routes, so the zone
    // is one tappable row that scales as more tools are added (favorites/active
    // highlight still guide you). Clicking the header expands it on demand.
    const accentGroupIds = useMemo(() => {
        const s = new Set<string>();
        for (const z of zones) if (z.accent) for (const g of z.groups) s.add(g.id);
        return s;
    }, [zones]);

    // undefined = no user preference (auto-expand active group)
    // null     = user explicitly collapsed everything
    // string   = user explicitly expanded this group
    const [userExpandedGroupId, setUserExpandedGroupId] = useState<string | null | undefined>(undefined);

    // If user hasn't interacted, auto-expand the active route's group — except an
    // accent (Practice) group, which stays collapsed until the user opens it.
    const autoExpandId = activeGroupId && accentGroupIds.has(activeGroupId) ? undefined : activeGroupId;
    const expandedGroupId = userExpandedGroupId !== undefined ? userExpandedGroupId : autoExpandId;

    // When forceExpanded is set (mobile drawer), it is always open.
    // `shut`, not `collapsed`: on Practice routes autoCollapse shuts it without
    // touching the saved preference, and the handle follows what is on screen.
    const shut = forceExpanded ? false : autoCollapse ? !peeked : collapsed;

    // Refs for icon animation targets — keyed by group id
    const iconRefs = useRef<Record<string, HTMLSpanElement | null>>({});

    // Handle navigation — reset user override so the new active group auto-expands
    const handleNavClick = useCallback((href: string) => {
        setUserExpandedGroupId(undefined);
        onNavigate(href);
        onMobileClose?.();
    }, [onNavigate, onMobileClose]);

    // Toggle group expand/collapse — with icon animation
    const handleGroupClick = useCallback((groupId: string, isSingleItem: boolean, singleHref?: string) => {
        // Trigger icon animation
        const iconEl = iconRefs.current[groupId];
        if (iconEl) {
            triggerIconAnim(iconEl, getAnimForGroup(groupId));
        }

        if (isSingleItem && singleHref) {
            handleNavClick(singleHref);
        } else {
            setUserExpandedGroupId(prev => prev === groupId ? null : groupId);
        }
    }, [handleNavClick]);

    const aside = (
        <aside className="relative flex flex-col h-full w-60 bg-white dark:bg-stone-900 border-r border-stone-200 dark:border-stone-800">

            {/* ─── Nav Zones & Groups ─── */}
            <nav className="flex-1 overflow-y-auto py-1">

                {/* ★ Favorites — pinned shortcuts (the veteran's fast path) */}
                {favoriteItems.length > 0 && (
                    <div className="mb-1">
                        <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-amber-500/80 uppercase tracking-[0.15em] flex items-center gap-1">
                            <Star size={11} className="fill-amber-400 text-amber-400" /> Favorites
                        </p>
                        {favoriteItems.map((item) => {
                            const isActive = activeRoute === item.href;
                            return (
                                <div key={`fav-${item.href}`} className="group/fav relative">
                                    <button
                                        onClick={() => handleNavClick(item.href)}
                                        aria-current={isActive ? 'page' : undefined}
                                        className={`
                                            w-full flex items-center gap-3 pl-4 pr-9 py-[6px] text-[12.5px] transition-all duration-150
                                            ${isActive
                                                ? 'text-teal-700 dark:text-teal-600 font-semibold bg-teal-50/80 dark:bg-teal-950/15 border-l-2 border-teal-600'
                                                : 'text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-800/30 border-l-2 border-transparent'
                                            }
                                        `}
                                    >
                                        <span className="shrink-0">{item.icon}</span>
                                        <span className="flex-1 text-left truncate">{item.label}</span>
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={`Unpin ${item.label}`}
                                        title="Unpin from favorites"
                                        onClick={() => handleToggleFavorite(item.href)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-amber-400 hover:text-amber-500"
                                    >
                                        <Star size={13} className="fill-amber-400" />
                                    </button>
                                </div>
                            );
                        })}
                        <div className="mx-3 mt-2 border-t border-stone-200/70 dark:border-stone-800" />
                    </div>
                )}

                {zones.map((zone, zi) => (
                    <div key={zi} className="mb-0.5">
                        {/* Zone header (only show if zone has a label) */}
                        {zone.label && (
                            <p className={`px-4 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.15em] ${zone.accent
                                ? 'text-teal-600/90 dark:text-teal-500'
                                : 'text-stone-400/70 dark:text-stone-500'}`}>
                                {zone.label}
                            </p>
                        )}

                        {/* Groups within zone */}
                        {zone.groups.map((group) => {
                            const isExpanded = expandedGroupId === group.id;
                            const hasActiveItem = group.items.some(item => item.href === activeRoute);
                            const isSingleItem = group.items.length === 1;

                            const singleHref = isSingleItem ? group.items[0].href : undefined;
                            const singleFav = singleHref ? isFavoriteHref(favorites, singleHref) : false;

                            return (
                                <div key={group.id}>
                                    {/* Group header — clickable to expand/collapse */}
                                    <div className="relative group/navrow">
                                        <button
                                            onClick={() => handleGroupClick(
                                                group.id,
                                                isSingleItem,
                                                singleHref
                                            )}
                                            aria-expanded={!isSingleItem ? isExpanded : undefined}
                                            aria-current={isSingleItem && hasActiveItem ? 'page' : undefined}
                                            className={`
                                                w-full flex items-center gap-3 px-4 py-2 text-[13px] transition-colors
                                                ${isSingleItem ? 'pr-9' : ''}
                                                ${hasActiveItem
                                                    ? 'text-stone-900 dark:text-stone-100 font-semibold'
                                                    : 'text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800/50'
                                                }
                                            `}
                                        >
                                            <span
                                                ref={(el) => { iconRefs.current[group.id] = el; }}
                                                className={`shrink-0 icon-click-target ${hasActiveItem ? 'text-teal-700' : ''}`}
                                            >
                                                {group.icon}
                                            </span>
                                                <span className="flex-1 text-left truncate">
                                                    {group.label}
                                                </span>
                                                {/* Gamified progress pill ("2/6") with a soft attention pulse —
                                                    the ambient "finish your setup" nudge (Zeigarnik, tastefully). */}
                                                {isSingleItem && group.items[0].progress && (
                                                    <span className="relative shrink-0 inline-flex items-center rounded-full bg-teal-600 dark:bg-teal-500 text-white text-[10px] font-bold tabular-nums px-2 py-0.5">
                                                        {group.items[0].progress.done}/{group.items[0].progress.total}
                                                        <span className="absolute -top-0.5 -right-0.5 flex w-2 h-2">
                                                            <span className="absolute inline-flex w-full h-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                                                            <span className="relative inline-flex w-2 h-2 rounded-full bg-amber-400" />
                                                        </span>
                                                    </span>
                                                )}
                                                {!isSingleItem && (
                                                    <span className="shrink-0 text-stone-300 dark:text-stone-600">
                                                        {isExpanded
                                                            ? <ChevronUp size={14} />
                                                            : <ChevronDown size={14} />
                                                        }
                                                    </span>
                                                )}
                                        </button>
                                        {/* Pin toggle for single-item groups (the leaf is the header) */}
                                        {isSingleItem && singleHref && (
                                            <button
                                                type="button"
                                                aria-label={singleFav ? `Unpin ${group.label}` : `Pin ${group.label}`}
                                                title={singleFav ? 'Unpin from favorites' : 'Pin to favorites'}
                                                onClick={() => handleToggleFavorite(singleHref)}
                                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity
                                                    ${singleFav
                                                        ? 'text-amber-400 opacity-100'
                                                        : 'text-stone-300 dark:text-stone-600 opacity-40 group-hover/navrow:opacity-100 hover:text-amber-400'
                                                    }`}
                                            >
                                                <Star size={13} className={singleFav ? 'fill-amber-400' : ''} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Expanded sub-items */}
                                    {isExpanded && !isSingleItem && (
                                        <div className="pb-1">
                                            {group.items.map((item) => {
                                                const isActive = activeRoute === item.href;
                                                const fav = isFavoriteHref(favorites, item.href);
                                                return (
                                                    <div key={item.id} className="relative group/navrow">
                                                        <button
                                                            onClick={() => handleNavClick(item.href)}
                                                            aria-current={isActive ? 'page' : undefined}
                                                            className={`
                                                                w-full flex items-center gap-3 pl-11 pr-9 py-[6px] text-[12.5px] transition-all duration-150
                                                                ${isActive
                                                                    ? 'text-teal-700 dark:text-teal-600 font-semibold bg-teal-50/80 dark:bg-teal-950/15 border-l-2 border-teal-600 !pl-[42px]'
                                                                    : 'text-stone-500 dark:text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800/30 border-l-2 border-transparent'
                                                                }
                                                            `}
                                                        >
                                                            <span className="flex-1 text-left truncate">{item.label}</span>
                                                            {!!item.badge && item.badge > 0 && (
                                                                <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[10px] font-semibold grid place-items-center group-hover/navrow:opacity-0 transition-opacity">
                                                                    {item.badge > 99 ? '99+' : item.badge}
                                                                </span>
                                                            )}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            aria-label={fav ? `Unpin ${item.label}` : `Pin ${item.label}`}
                                                            title={fav ? 'Unpin from favorites' : 'Pin to favorites'}
                                                            onClick={() => handleToggleFavorite(item.href)}
                                                            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity
                                                                ${fav
                                                                    ? 'text-amber-400 opacity-100'
                                                                    : 'text-stone-300 dark:text-stone-600 opacity-40 group-hover/navrow:opacity-100 hover:text-amber-400'
                                                                }`}
                                                        >
                                                            <Star size={13} className={fav ? 'fill-amber-400' : ''} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </nav>


            {/* ─── Plan & billing indicator ─── */}
            <div className="px-2 py-2 border-t border-stone-100 dark:border-stone-800/60">
                <PlanBadge onNavigated={onMobileClose} />
            </div>
        </aside>
    );

    if (forceExpanded) return aside;
    return (
        <SideDrawer open={!shut} onOpenChange={setOpen} openLabel="Expand menu" closeLabel="Collapse menu" name="Menu">
            {aside}
        </SideDrawer>
    );
}

